class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.observeCartChanges();
  }

  observeCartChanges() {
    const cartItemsContainer = document.getElementById('CartDrawer');

    if (!cartItemsContainer) {
      console.warn('Cart items container not found for observer');
      return;
    }

    this.cartObserver = new MutationObserver(async (mutations) => {
      if (mutations.some(m => m.type === 'childList')) {
        await this.cleanupOrphanedCones();
      }
    });

    this.cartObserver.observe(cartItemsContainer, {
      childList: true,
      subtree: true
    });

  
    document.addEventListener('cart-drawer:open', () => {
      setTimeout(() => this.cleanupOrphanedCones(), 300);
    }, { once: false });
  }

  async cleanupOrphanedCones() {
  try {
    const cartRes = await fetch('/cart.js');
    const cart = await cartRes.json();

    if (!cart || !cart.items || cart.items.length === 0) return;

    const updates = {};

    const yarnKeys = new Set();
    cart.items.forEach(item => {
      if (!item.properties || !item.properties['_linked_id']) {
        yarnKeys.add(item.key);
      }
    });

    cart.items.forEach(item => {
      const linkedId = item.properties?._linked_to_product_id;
      if (linkedId) {
        const parentExists = cart.items.some(yarnItem => {
          return yarnItem.variant_id == linkedId && !yarnItem.properties?._linked_to_product_id;
      });

        if (!parentExists) {
          updates[item.key] = 0;
        }
      }
    });

    if (Object.keys(updates).length === 0) return;

    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    });

    const sectionsRes = await fetch('/?sections=cart-drawer,cart-icon-bubble');
    const sectionsJson = await sectionsRes.json();

    const drawer = this.closest('cart-drawer');
    if (drawer && typeof drawer.renderContents === 'function') {
      drawer.renderContents({ sections: sectionsJson });
    } else {
      const drawerInner = document.querySelector('#CartDrawer .drawer__inner');
      if (drawerInner && sectionsJson['cart-drawer']) {
        drawerInner.innerHTML = sectionsJson['cart-drawer'];
      }
      const bubble = document.querySelector('#cart-icon-bubble');
      if (bubble && sectionsJson['cart-icon-bubble']) {
        bubble.innerHTML = sectionsJson['cart-icon-bubble'];
      }
    }

    console.log('Orphaned cones removed and drawer refreshed');

  } catch (err) {
    console.error('Error when clearing dependent cones:', err);
  }
}

  disconnectedCallback() {
    super.disconnectedCallback?.();
    if (this.cartObserver) {
      this.cartObserver.disconnect();
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
